import { FunctionComponent, useMemo, useState } from 'react';
import { ExternalAuthentication } from '../../../../@types/externalAuthentication';
import { SelectItem } from 'components/util/select/selectData';
import { SingleSelect } from 'components/util/select/SingleSelect';
import { useTranslator } from 'translation/useTranslator';
import { Text } from 'components/text/Text';
import { Stack } from '@mui/material';
import { TextField } from '@nps.today/component-library';

interface ExternalAuthenticationMethodFormProps {
  externalAuthentication: ExternalAuthentication;
  onUpdate: (externalAuthentication: ExternalAuthentication) => void;
}

export const ExternalAuthenticationMethodForm: FunctionComponent<
  ExternalAuthenticationMethodFormProps
> = ({ externalAuthentication, onUpdate }) => {
  const translator = useTranslator();

  const [hasEditedValue, setHasEditedValue] = useState(false);

  const valueLabel = useMemo<string>(() => {
    if (externalAuthentication.type === 'ApiKey') {
      return translator.apiKey();
    }
    if (externalAuthentication.type === 'Basic') {
      return translator.password();
    }
    return translator.value();
  }, [externalAuthentication.type, translator]);

  const getSecretValue = (
    externalAuthentication: ExternalAuthentication,
    hasEditedValue: boolean
  ): string => {
    const isEditing = Boolean(externalAuthentication.id);
    const noEditsMade = !hasEditedValue && !externalAuthentication.value;
    if (isEditing && noEditsMade) {
      return '••••••••';
    }
    return externalAuthentication.value;
  };

  const dataTestIdPrefix = externalAuthentication.id
    ? 'edit-external-authentication-method-dialog'
    : 'add-external-authentication-method-dialog';

  return (
    <Stack gap={1}>
      <TextField
        dataTestId={`${dataTestIdPrefix}-name`}
        fullWidth
        value={externalAuthentication.name}
        onChange={(event) =>
          onUpdate({
            ...externalAuthentication,
            name: event.target.value
          })
        }
        label={translator.name()}
        margin={'normal'}
        color={'primary'}
        required
        helperText={translator.giveTheAuthenticationMethodADescriptiveName()}
      />
      <SingleSelect
        dataTestId={`${dataTestIdPrefix}-type`}
        items={[
          { value: 'Basic', displayValue: 'Basic' },
          { value: 'Bearer', displayValue: 'Bearer' },
          { value: 'ApiKey', displayValue: 'Api Key' },
          { value: 'CustomAuthHeader', displayValue: 'Custom Authentication Header' }
        ]}
        value={externalAuthentication.type}
        label={translator.authenticationType()}
        onChange={(newItem: SelectItem) => {
          onUpdate({
            ...externalAuthentication,
            type: newItem.value as ExternalAuthentication['type']
          });
        }}
      />
      {externalAuthentication.type === 'Basic' && (
        <TextField
          dataTestId={`${dataTestIdPrefix}-basic-auth-username`}
          fullWidth
          value={externalAuthentication.basicAuthUsername}
          onChange={(event) =>
            onUpdate({
              ...externalAuthentication,
              basicAuthUsername: event.target.value
            })
          }
          label={translator.username()}
          margin={'normal'}
          color={'primary'}
          required
        />
      )}
      {externalAuthentication.type === 'CustomAuthHeader' && (
        <>
          <TextField
            dataTestId={`${dataTestIdPrefix}-custom-auth-header-name`}
            fullWidth
            value={externalAuthentication.customAuthenticationHeaderName}
            onChange={(event) =>
              onUpdate({
                ...externalAuthentication,
                customAuthenticationHeaderName: event.target.value
              })
            }
            label={translator.authenticationHeaderName()}
            margin={'normal'}
            color={'primary'}
            required
          />
          <TextField
            dataTestId={`${dataTestIdPrefix}-custom-auth-header-value`}
            fullWidth
            value={externalAuthentication.customAuthenticationHeaderScheme}
            onChange={(event) =>
              onUpdate({
                ...externalAuthentication,
                customAuthenticationHeaderScheme: event.target.value
              })
            }
            label={translator.authenticationHeaderScheme()}
            margin={'normal'}
            color={'primary'}
          />
        </>
      )}
      <TextField
        dataTestId={`${dataTestIdPrefix}-value`}
        fullWidth
        value={getSecretValue(externalAuthentication, hasEditedValue)}
        onChange={(event) => {
          if (hasEditedValue) {
            onUpdate({
              ...externalAuthentication,
              value: event.target.value
            });
          } else {
            setHasEditedValue(true);
            onUpdate({
              ...externalAuthentication,
              value: event.target.value
            });
          }
        }}
        onFocus={() => {
          if (externalAuthentication.id && !hasEditedValue) {
            setHasEditedValue(true);
            onUpdate({
              ...externalAuthentication,
              value: ''
            });
          }
        }}
        label={valueLabel}
        margin={'normal'}
        color={'primary'}
        required
        type="password"
      />
      {externalAuthentication.type === 'CustomAuthHeader' && (
        <Text>
          {translator.yourCustomAuthenticationHeaderWillLookLikeThis()}
          <Text fontStyle={'italic'}>
            "{externalAuthentication.customAuthenticationHeaderName}" : "
            {externalAuthentication.customAuthenticationHeaderScheme} ••••••"
          </Text>
        </Text>
      )}
    </Stack>
  );
};
